/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import i18n from "i18next";

import { createClient, type MatrixClient } from "matrix-js-sdk";
import { getProductName } from "../branding";
import type { Session } from "../ClientContext";
import { Config } from "../config/Config";
import { backendFetch, backendUrl } from "../utils/backend";
import { joinAppearance } from "./joinAppearance";
import { pendingJoinHandoff } from "./captureJoinHandoff";

// Use the password only during the ordinary cabinet login. Only the paired
// Matrix token reaches the backend; passwords are never persisted.
export async function prepareJoinSession(
  client: MatrixClient,
  password: string,
): Promise<void> {
  const authClient = createClient({ baseUrl: client.getHomeserverUrl() });
  const login = await authClient.login("m.login.password", {
    identifier: { type: "m.id.user", user: client.getUserId()! },
    password,
    initial_device_display_name: `${getProductName()} · join`,
  });
  try {
    await backendFetch(client, "/api/auth/join-session/prepare", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_token: login.access_token,
        device_id: login.device_id,
      }),
    });
  } catch (error) {
    authClient.setAccessToken(login.access_token);
    await authClient.logout().catch(() => {});
    throw error;
  }
}

export function guestOrigin(): string {
  const configured = import.meta.env.VITE_GUEST_URL;
  if (!configured) throw new Error(i18n.t("instance.ui.join_not_configured"));
  const url = new URL(configured);
  if (
    url.username ||
    url.password ||
    url.pathname !== "/" ||
    url.search ||
    url.hash ||
    !["https:", "http:"].includes(url.protocol)
  )
    throw new Error(i18n.t("instance.ui.invalid_join_address"));
  return url.origin;
}

export function joinTarget(
  relative: string,
  origin: string,
  userId: string,
  code?: string,
): string {
  const url = new URL(relative, origin);
  if (url.origin !== origin)
    throw new Error(i18n.t("instance.ui.unexpected_join"));
  for (const name of ["displayName", "homeserver", "staffUser"])
    url.searchParams.delete(name);
  url.searchParams.set("staffUser", userId);
  const index = url.hash.indexOf("?");
  const prefix = index >= 0 ? url.hash.slice(0, index) : "#";
  const params = new URLSearchParams(
    index >= 0 ? url.hash.slice(index + 1) : "",
  );
  for (const name of ["displayName", "homeserver", "handoff"])
    params.delete(name);
  if (code) params.set("handoff", code);
  url.hash = prefix + "?" + params.toString();
  return url.href;
}

export interface JoinBrowser {
  challenge: string;
  userId?: string;
}
export async function joinBrowserChallenge(
  origin: string,
  signal: AbortSignal,
): Promise<JoinBrowser> {
  return new Promise((resolve, reject) => {
    const nonce = crypto.randomUUID(),
      frame = document.createElement("iframe");
    frame.hidden = true;
    frame.title = i18n.t("instance.ui.organizer_connecting");
    frame.src = origin + "/auth/bridge";
    const cleanup = (): void => {
      clearTimeout(timer);
      window.removeEventListener("message", message);
      signal.removeEventListener("abort", abort);
      frame.remove();
    };
    const abort = (): void => {
      cleanup();
      reject(new DOMException("Join cancelled", "AbortError"));
    };
    const hello = (): void =>
      frame.contentWindow?.postMessage(
        { type: "element-join-hello", nonce, appearance: joinAppearance() },
        origin,
      );
    const message = (event: MessageEvent): void => {
      if (event.origin !== origin || event.source !== frame.contentWindow)
        return;
      if (event.data?.type === "element-join-ready") {
        hello();
        return;
      }
      if (
        event.data?.type !== "element-join-challenge" ||
        event.data.nonce !== nonce ||
        !/^[A-Za-z0-9_-]{43}$/.test(event.data.challenge)
      )
        return;
      cleanup();
      resolve({ challenge: event.data.challenge, userId: event.data.userId });
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(i18n.t("instance.ui.handoff_failed")));
    }, 30000);
    window.addEventListener("message", message);
    signal.addEventListener("abort", abort, { once: true });
    frame.onload = hello;
    if (signal.aborted) {
      abort();
      return;
    }
    document.body.append(frame);
  });
}

// The ticket is only a short-lived pairing proof. Each browser origin keeps
// its own Matrix token; neither token is sent through postMessage.
export async function adoptExistingJoinSession(
  origin: string,
  ticket: string,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const nonce = crypto.randomUUID();
    const frame = document.createElement("iframe");
    frame.hidden = true;
    frame.title = i18n.t("instance.ui.organizer_connecting");
    frame.src = origin + "/auth/bridge";
    const cleanup = (): void => {
      clearTimeout(timer);
      window.removeEventListener("message", message);
      signal.removeEventListener("abort", abort);
      frame.remove();
    };
    const abort = (): void => {
      cleanup();
      reject(new DOMException("Join cancelled", "AbortError"));
    };
    const hello = (): void =>
      frame.contentWindow?.postMessage(
        { type: "element-join-adopt", nonce, ticket },
        origin,
      );
    const message = (event: MessageEvent): void => {
      if (event.origin !== origin || event.source !== frame.contentWindow)
        return;
      if (event.data?.type === "element-join-ready") {
        hello();
        return;
      }
      if (
        event.data?.type !== "element-join-adopt-result" ||
        event.data.nonce !== nonce
      )
        return;
      cleanup();
      if (event.data.success === true) resolve();
      else reject(new Error("Could not preserve the existing join session"));
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("Join session preservation timed out"));
    }, 5000);
    window.addEventListener("message", message);
    signal.addEventListener("abort", abort, { once: true });
    frame.onload = hello;
    if (signal.aborted) {
      abort();
      return;
    }
    document.body.append(frame);
  });
}

export async function restoreJoinHandoff(): Promise<void> {
  if (!pendingJoinHandoff) return;
  const { code, roomId, userId, recordingId, accountOnly } = pendingJoinHandoff;
  if (window.location.origin !== guestOrigin())
    throw new Error(i18n.t("instance.ui.unexpected_origin"));
  const response = await fetch("/api/auth/join-session/exchange", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error(i18n.t("instance.ui.handoff_expired"));
  const data = (await response.json()) as {
    session: Session;
    matrixRoomId?: string;
    recordingId?: string;
    sessionOnly?: boolean;
  };
  if (
    (accountOnly
      ? data.sessionOnly!==true || !!data.matrixRoomId || !!data.recordingId
      : recordingId
        ? recordingId !== data.recordingId
        : roomId !== data.matrixRoomId) ||
    data.session.user_id !== userId ||
    !data.session.access_token ||
    !data.session.device_id ||
    new URL(data.session.homeserver_url!).origin !==
      new URL(Config.defaultHomeserverUrl()!).origin
  )
    throw new Error(i18n.t("instance.ui.join_verification"));
  const raw = localStorage.getItem("matrix-auth-store");
  const previous = raw ? (JSON.parse(raw) as Session) : undefined;
  // A verified one-use handoff from the authenticated cabinet intentionally
  // replaces a stale join identity. Names/URL parameters alone cannot do this.
  const sameDevice =
    previous?.user_id === data.session.user_id &&
    previous?.device_id === data.session.device_id &&
    previous?.access_token === data.session.access_token;
  localStorage.setItem(
    "matrix-auth-store",
    JSON.stringify({ ...data.session, reset_crypto: !sameDevice }),
  );
}

export async function revokeJoinSession(client: MatrixClient): Promise<void> {
  const response = await fetch(`${backendUrl}/api/auth/join-session/revoke`, {
    method: "POST",
    headers: { Authorization: `Bearer ${client.getAccessToken()}` },
  });
  if (!response.ok) throw new Error("Could not revoke the paired session");
}
